import { api } from "./client";
import type { SubscriptionStatus, EntitlementAccess } from "@mycompbox/shared";

/**
 * The caller's own subscription state, from GET /api/v1/billing/subscription.
 *
 * Deliberately narrow: it carries nothing that identifies the organization to
 * Stripe. The server omits `stripeCustomerId` and `stripeSubscriptionId`
 * because those are what an attacker would need in order to impersonate a
 * customer to Stripe support.
 */
export interface SubscriptionState {
  subscriptionStatus: SubscriptionStatus;
  /** ISO-8601, or null when no billing period has been established. */
  currentPeriodEnd: string | null;
  seatQuantity: number;
  /**
   * What this caller may currently do.
   *
   * Taken from the server rather than derived from `subscriptionStatus` here.
   * The status → access mapping is a revenue-critical rule that already exists
   * once, on the server, where the gate enforces it; deriving it a second time
   * in the client would let the UI and the API disagree about who is blocked.
   */
  access: EntitlementAccess;
  /** True when the caller has no organization (e.g. the bootstrap admin). */
  organizationless: boolean;
  /** free | team | enterprise | legacy (null when organization-less). */
  plan: "free" | "team" | "enterprise" | "legacy" | null;
  trialEndsAt: string | null;
  /** Days left in a no-card Team trial; null when not trialing. */
  trialDaysLeft: number | null;
  trialOver: boolean;
  /** Active people in the organization (the Free plan's limit counts these). */
  people: number;
  hasSubscription: boolean;
  managedAiWaitlisted: boolean;
  /** Whether online payment (Stripe checkout) is set up on the server. */
  checkoutAvailable: boolean;
  pricing: { freePeopleLimit: number; teamPricePerSeatUsd: number; trialDays: number };
}

interface SubscriptionResponse {
  data: SubscriptionState;
}

/**
 * True when an error is the entitlement gate refusing a write.
 *
 * Distinguishes a subscription refusal from every other 403 the API can
 * produce — a permissions failure, a scope violation, the invite-only signup
 * gate. Those are unrelated and must not be reported as billing problems.
 *
 * Keyed on the billing route appearing in the server's message, which is the
 * one thing every entitlement refusal includes and no other 403 does. A
 * dedicated error code would be sturdier; this deliberately does not invent one
 * unilaterally, since the server's error contract is shared with the CLI and
 * MCP.
 */
export function isEntitlementError(error: unknown): boolean {
  if (!error || typeof error !== "object") {
    return false;
  }

  const response = (error as { response?: { status?: number } }).response;
  if (response?.status !== 403) {
    return false;
  }

  const message = (error as { message?: unknown }).message;
  return typeof message === "string" && message.includes("/api/v1/billing");
}

export const billingApi = {
  /**
   * Read the caller's subscription state.
   *
   * Reachable under every subscription status — `/api/v1/billing` is on the
   * server's entitlement allowlist, so this call still succeeds for a customer
   * who is otherwise blocked. That is what lets the billing screen render at
   * the moment it is most needed.
   */
  subscription: () => api.get<SubscriptionResponse>("/billing/subscription"),
  /** POST /api/v1/billing/checkout — Stripe Checkout URL for the Team plan (org admins). */
  checkout: () => api.post<{ data: { url: string } }>("/billing/checkout", {}),
  /** POST /api/v1/billing/portal — Stripe customer portal URL (org admins). */
  portal: () => api.post<{ data: { url: string } }>("/billing/portal", {}),
};
